/**
 * contract.js - which boxes a game owes the engine, per level.
 *
 * PURE.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS IN THE ENGINE
 *
 * It was `bubble-pop/logic/boxes.js` until a second game needed the same
 * answers and imported it across a game boundary. Nothing in it is about
 * bubbles: it is pure derivation from `framework` and `judgement`, and the
 * answer must be identical in every game or the same box means different
 * things in different corners of one child's chart (section 9.1).
 *
 * ---------------------------------------------------------------------------
 * WHY A GAME SHOULD WORK THIS OUT RATHER THAN KNOW IT
 *
 * POST /api/engine/attempts refuses an attempt that carries the wrong set of
 * components - a missing box or an extra one - and refuses the WHOLE attempt
 * rather than writing part of it, because a partial write leaves a permanent
 * hole in the chart (ENGINE-BUILD.md section 6.2). That refusal is correct and
 * it is also silent to the child: they say the word, the bubble pops, and
 * nothing reaches the chart.
 *
 * The set is not constant, even inside one phase: Phase 7 Level 1 has four
 * boxes and Level 3 has five. It also depends on what the game declared it
 * instruments. So the set is asked for, per level, and checked before posting.
 * ENGINE-BUILD.md section 3.4 puts it plainly: always ask
 * getAssessableBoxes(); never hardcode.
 */

import {
  getBoxesForLevel,
  getAssessableBoxes,
  getLevel,
  getPromptStyle,
  getStrictness,
  boxState,
} from './framework.js';
import { JUDGED_BY_ENGINE } from './judgement.js';

/**
 * Boxes the ENGINE judges from the trial record at this level, given what the
 * game declares. The game must not send these.
 */
export function engineJudgedCodes(phase, level, capabilities) {
  const judgeable = JUDGED_BY_ENGINE[phase] || [];
  return getAssessableBoxes(phase, level, capabilities)
    .filter((b) => b.requires && judgeable.includes(b.code))
    .map((b) => b.code);
}

/** Boxes the GAME must judge and send. */
export function gameOwedCodes(phase, level, capabilities) {
  const engineCodes = engineJudgedCodes(phase, level, capabilities);
  return getAssessableBoxes(phase, level, capabilities)
    .map((b) => b.code)
    .filter((code) => !engineCodes.includes(code));
}

/**
 * The capabilities whose trial fields are MANDATORY at this level.
 *
 * A Phase 3 level needs neither, even though this game declares both: no box
 * at Phase 3 depends on instrumentation. Requiring the fields anyway would
 * make the record harder to produce for no measurement in return - and
 * judgeTrial() applies exactly this rule server-side, so matching it here is
 * what keeps the client's own validation honest.
 */
export function requiredCapsFor(phase, level, capabilities) {
  return [...new Set(
    getAssessableBoxes(phase, level, capabilities)
      .filter((b) => b.requires)
      .map((b) => b.requires)
  )];
}

/**
 * Check a component object before it is posted.
 *
 * The same check scoring.scoreAttempt() runs, so a mismatch is caught in the
 * game - where the level and the code that built it are both in front of you -
 * instead of arriving as a 400 during a session with a child.
 *
 * @returns {{ok:boolean, errors:string[], expected:string[]}}
 */
export function checkComponents({ phase, level, capabilities, components }) {
  const expected = gameOwedCodes(phase, level, capabilities);
  const supplied = Object.keys(components || {});
  const errors = [];

  for (const code of expected) {
    if (!supplied.includes(code)) {
      errors.push(`Missing component ${code} for phase ${phase} level ${level}.`);
    }
  }
  for (const code of supplied) {
    if (!expected.includes(code)) {
      const box = getBoxesForLevel(phase, level).find((b) => b.code === code);
      errors.push(
        box
          ? `Component ${code} (${box.name}) must not be sent: it is ${boxState(box, capabilities)}.`
          : `Component ${code} does not exist at phase ${phase} level ${level}.`
      );
    }
  }

  return { ok: errors.length === 0, errors, expected };
}

/** Every box of the level with its state, for the grown-ups panel. */
export function levelBoxSummary(phase, level, capabilities) {
  const engineCodes = engineJudgedCodes(phase, level, capabilities);
  return getBoxesForLevel(phase, level).map((box) => ({
    boxId: box.boxId,
    code: box.code,
    name: box.name,
    state: boxState(box, capabilities),
    judgedBy: box.observational
      ? 'nobody'
      : engineCodes.includes(box.code)
        ? 'engine'
        : boxState(box, capabilities) === 'scored' ? 'game' : 'nobody',
    reason: box.observationalReason || null,
  }));
}

/**
 * Everything a game needs to know about one level, in one call.
 *
 * A game asks this at the start of a level and never hardcodes any of it: the
 * box set is not constant even inside one phase (Phase 7 Level 1 has four
 * boxes, Level 3 has five), and which of them the game owes depends on what
 * the game declared it instruments.
 *
 * @param {number} phase
 * @param {number} level
 * @param {object} capabilities  the game's declaration
 * @returns {{phase:number, level:number, intent:object, prompt:object,
 *            strictness:object, gameJudges:string[], engineJudges:string[],
 *            requiredCapabilities:string[], boxes:Array}}
 */
export function describeLevelContract(phase, level, capabilities) {
  return {
    phase,
    level,
    intent: getLevel(phase, level),
    prompt: getPromptStyle(phase),
    strictness: getStrictness(phase),
    // The boxes the game must judge and send, and the ones it must NOT send:
    // posting an engine-judged box is refused with a 400 that the child never
    // sees.
    gameJudges: gameOwedCodes(phase, level, capabilities),
    engineJudges: engineJudgedCodes(phase, level, capabilities),
    // Trial-record fields this level obliges, given the declaration.
    requiredCapabilities: requiredCapsFor(phase, level, capabilities),
    boxes: levelBoxSummary(phase, level, capabilities),
  };
}

export default {
  engineJudgedCodes,
  gameOwedCodes,
  requiredCapsFor,
  checkComponents,
  levelBoxSummary,
  describeLevelContract,
};
